import {
	Button,
	Empty,
	Form,
	Input,
	InputNumber,
	Modal,
	message,
	Select,
	Space,
	Tree,
	Typography,
} from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type JsonPath, pathToKey } from '../utils/jsonPath';

const { Text } = Typography;

const ROOT_KEY = 'config.json';
const VALUE_TYPES = [
	'string',
	'number',
	'boolean',
	'null',
	'object',
	'array',
] as const;

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue =
	| JsonPrimitive
	| JsonValue[]
	| { [key: string]: JsonValue };
type JsonValueType = (typeof VALUE_TYPES)[number];

interface JsonTreeEditorProps {
	value: JsonValue | null;
	onChange: (value: JsonValue) => void;
	disabled?: boolean;
	height?: number;
	onNodeActivate?: (pathKey: string) => void;
}

interface EditorState {
	mode: 'add' | 'edit';
	parentPath: JsonPath;
	targetPath?: JsonPath;
	parentKind: 'object' | 'array' | 'root';
	initialType: JsonValueType;
	initialKey: string;
	initialValue: string | number | boolean | null;
}

interface EditorFormValues {
	key?: string;
	type?: JsonValueType;
	value?: string | number | boolean | null;
}

function isObjectValue(
	value: JsonValue,
): value is { [key: string]: JsonValue } {
	return !Array.isArray(value) && typeof value === 'object' && value !== null;
}

function isJsonPrimitive(value: JsonValue): value is JsonPrimitive {
	return (
		value === null ||
		typeof value === 'string' ||
		typeof value === 'number' ||
		typeof value === 'boolean'
	);
}

function hasOwnKey(
	value: { [key: string]: JsonValue },
	key: string,
): boolean {
	return Object.keys(value).includes(key);
}

function getJsonValueType(value: JsonValue): JsonValueType {
	if (value === null) {
		return 'null';
	}

	if (Array.isArray(value)) {
		return 'array';
	}

	switch (typeof value) {
		case 'string':
			return 'string';
		case 'number':
			return 'number';
		case 'boolean':
			return 'boolean';
		default:
			return 'object';
	}
}

function getJsonTypeLabel(type: JsonValueType) {
	switch (type) {
		case 'string':
			return '字符串';
		case 'number':
			return '数字';
		case 'boolean':
			return '布尔';
		case 'null':
			return '空值';
		case 'object':
			return '对象';
		case 'array':
			return '数组';
	}
}

function formatJsonPrimitive(value: JsonPrimitive) {
	if (value === null) {
		return 'null';
	}

	if (typeof value === 'string') {
		return JSON.stringify(value);
	}

	return String(value);
}

function normalizeFormValue(type: JsonValueType, value: unknown): JsonValue {
	switch (type) {
		case 'string':
			return typeof value === 'string' ? value : '';
		case 'number': {
			const nextValue = typeof value === 'number' ? value : Number(value);
			if (Number.isNaN(nextValue)) {
				throw new Error('请输入有效数字');
			}

			return nextValue;
		}
		case 'boolean':
			return value === true || value === 'true';
		case 'null':
			return null;
		case 'object':
			return {};
		case 'array':
			return [];
	}
}

function getValueAtPath(value: JsonValue, path: JsonPath): JsonValue {
	return path.reduce<JsonValue>((current, segment) => {
		if (Array.isArray(current) && typeof segment === 'number') {
			return current[segment];
		}

		if (isObjectValue(current) && typeof segment === 'string') {
			return current[segment];
		}

		throw new Error('JSON 路径无效');
	}, value);
}

function replaceValueAtPath(
	value: JsonValue,
	path: JsonPath,
	nextValue: JsonValue,
) {
	if (path.length === 0) {
		return nextValue;
	}

	const [head, ...rest] = path;

	if (Array.isArray(value)) {
		if (typeof head !== 'number') {
			throw new Error('JSON 路径无效');
		}

		const nextArray = [...value];
		nextArray[head] =
			rest.length === 0
				? nextValue
				: replaceValueAtPath(nextArray[head], rest, nextValue);
		return nextArray;
	}

	if (isObjectValue(value)) {
		if (typeof head !== 'string') {
			throw new Error('JSON 路径无效');
		}

		const nextObject = { ...value };
		nextObject[head] =
			rest.length === 0
				? nextValue
				: replaceValueAtPath(nextObject[head], rest, nextValue);
		return nextObject;
	}

	throw new Error('JSON 路径无效');
}

function deleteValueAtPath(value: JsonValue, path: JsonPath): JsonValue {
	if (path.length === 0) {
		throw new Error('不能删除根节点');
	}

	const parentPath = path.slice(0, -1);
	const last = path[path.length - 1];
	const parent = getValueAtPath(value, parentPath);

	if (Array.isArray(parent) && typeof last === 'number') {
		const nextArray = [...parent];
		nextArray.splice(last, 1);
		return replaceValueAtPath(value, parentPath, nextArray);
	}

	if (isObjectValue(parent) && typeof last === 'string') {
		const nextObject = { ...parent };
		delete nextObject[last];
		return replaceValueAtPath(value, parentPath, nextObject);
	}

	throw new Error('JSON 路径无效');
}

function insertChildAtPath(
	value: JsonValue,
	parentPath: JsonPath,
	parentKind: 'object' | 'array' | 'root',
	key: string,
	childValue: JsonValue,
) {
	if (parentKind === 'array') {
		const parent = getValueAtPath(value, parentPath);

		if (!Array.isArray(parent)) {
			throw new Error('目标不是数组');
		}

		return replaceValueAtPath(value, parentPath, [...parent, childValue]);
	}

	const parent =
		parentKind === 'root' ? value : getValueAtPath(value, parentPath);

	if (!isObjectValue(parent)) {
		throw new Error('目标不是对象');
	}

	if (!key) {
		throw new Error('请输入字段名');
	}

	if (hasOwnKey(parent, key)) {
		throw new Error(`字段名 ${key} 已存在`);
	}

	return replaceValueAtPath(value, parentPath, {
		...parent,
		[key]: childValue,
	});
}

function collectExpandableKeys(
	value: JsonValue,
	path: JsonPath,
	keys: string[],
) {
	if (Array.isArray(value)) {
		keys.push(pathToKey(path));
		value.forEach((item, index) => {
			collectExpandableKeys(item, [...path, index], keys);
		});
		return;
	}

	if (isObjectValue(value)) {
		keys.push(pathToKey(path));
		Object.entries(value).forEach(([childKey, childValue]) => {
			collectExpandableKeys(childValue, [...path, childKey], keys);
		});
	}
}

function JsonTreeNodeTitle({
	label,
	summary,
	canAdd,
	canEdit,
	canDelete,
	isExpandable,
	onAdd,
	onEdit,
	onDelete,
	onActivate,
	onToggleExpand,
}: {
	label: string;
	summary: string;
	canAdd: boolean;
	canEdit: boolean;
	canDelete: boolean;
	isExpandable: boolean;
	onAdd?: () => void;
	onEdit?: () => void;
	onDelete?: () => void;
	onActivate?: () => void;
	onToggleExpand?: () => void;
}) {
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: 12,
				width: '100%',
				cursor: 'pointer',
			}}
			onClick={() => {
				onActivate?.();
				if (isExpandable) {
					onToggleExpand?.();
				}
			}}
		>
			<Space size={8} wrap>
				<Text strong>{label}</Text>
				<Text type="secondary">{summary}</Text>
			</Space>
			<Space size={4} onClick={(event) => event.stopPropagation()}>
				{canAdd ? (
					<Button size="small" type="text" onClick={onAdd}>
						新增
					</Button>
				) : null}
				{canEdit ? (
					<Button size="small" type="text" onClick={onEdit}>
						编辑
					</Button>
				) : null}
				{canDelete ? (
					<Button size="small" type="text" danger onClick={onDelete}>
						删除
					</Button>
				) : null}
			</Space>
		</div>
	);
}

export default function JsonTreeEditor({
	value,
	onChange,
	disabled = false,
	height = 640,
	onNodeActivate,
}: JsonTreeEditorProps) {
	const [expandedKeys, setExpandedKeys] = useState<string[]>([ROOT_KEY]);
	const [autoExpandParent, setAutoExpandParent] = useState(false);
	const [editorState, setEditorState] = useState<EditorState | null>(null);
	const [form] = Form.useForm<EditorFormValues>();

	useEffect(() => {
		if (value === null) {
			setExpandedKeys([]);
			return;
		}

		setExpandedKeys([ROOT_KEY]);
		setAutoExpandParent(false);
	}, [value]);

	const handleOpenAddEditor = useCallback(
		(parentPath: JsonPath, parentKind: 'object' | 'array' | 'root') => {
			setEditorState({
				mode: 'add',
				parentPath,
				parentKind,
				initialType: 'string',
				initialKey: '',
				initialValue: '',
			});
		},
		[],
	);

	const handleOpenEditEditor = useCallback(
		(path: JsonPath) => {
			if (value === null) {
				return;
			}

			const currentValue = getValueAtPath(value, path);
			const currentType = getJsonValueType(currentValue);

			if (currentType === 'object' || currentType === 'array') {
				message.info(
					'当前仅支持编辑字符串、数字、布尔和空值，容器节点请通过新增/删除维护。',
				);
				return;
			}

			setEditorState({
				mode: 'edit',
				parentPath: path.slice(0, -1),
				targetPath: path,
				parentKind:
					path.length === 0
						? 'root'
						: typeof path[path.length - 1] === 'number'
							? 'array'
							: 'object',
				initialType: currentType,
				initialKey:
					typeof path[path.length - 1] === 'string'
						? String(path[path.length - 1])
						: '',
				initialValue: isJsonPrimitive(currentValue) ? currentValue : null,
			});
		},
		[value],
	);

	const handleDeleteNode = useCallback(
		(path: JsonPath) => {
			if (value === null) {
				return;
			}

			Modal.confirm({
				title: '确认删除',
				content: '确定要删除这个节点吗？',
				okText: '删除',
				okButtonProps: { danger: true },
				cancelText: '取消',
				onOk: () => {
					try {
						onChange(deleteValueAtPath(value, path));
					} catch (error) {
						message.error(error instanceof Error ? error.message : '删除失败');
					}
				},
			});
		},
		[onChange, value],
	);

	const handleConfirmEditor = useCallback(async () => {
		if (value === null || !editorState) {
			return;
		}

		try {
			const fields = await form.validateFields();
			const nextType = fields.type as JsonValueType;
			const nextKey = typeof fields.key === 'string' ? fields.key.trim() : '';
			let nextValue: JsonValue;

			if (editorState.mode === 'edit') {
				nextValue = normalizeFormValue(nextType, fields.value);

				const updated = replaceValueAtPath(
					value,
					editorState.targetPath || [],
					nextValue,
				);
				onChange(updated);
				setEditorState(null);
				return;
			}

			nextValue = normalizeFormValue(nextType, fields.value);

			const updated = insertChildAtPath(
				value,
				editorState.parentPath,
				editorState.parentKind,
				nextKey,
				nextValue,
			);

			onChange(updated);
			setExpandedKeys((keys) => {
				const parentKey = pathToKey(editorState.parentPath);
				return keys.includes(parentKey) ? keys : [...keys, parentKey];
			});
			setEditorState(null);
		} catch (error) {
			if (error instanceof Error) {
				message.error(error.message);
			}
		}
	}, [editorState, form, onChange, value]);

	const treeData = useMemo<DataNode[]>(() => {
		if (value === null) {
			return [];
		}

		const buildNode = (
			currentValue: JsonValue,
			path: JsonPath,
			label: string,
		): DataNode => {
			const valueType = getJsonValueType(currentValue);
			const isContainer = valueType === 'object' || valueType === 'array';
			const nodeKey = pathToKey(path);
			let summary = '';
			let children: DataNode[] | undefined;

			if (isContainer) {
				if (Array.isArray(currentValue)) {
					summary = `数组(${currentValue.length})`;
					children = currentValue.map((item, index) =>
						buildNode(item, [...path, index], `[${index}]`),
					);
				} else {
					const objectValue = currentValue as { [key: string]: JsonValue };
					summary = `对象(${Object.keys(objectValue).length})`;
					children = Object.entries(objectValue).map(([childKey, childValue]) =>
						buildNode(childValue, [...path, childKey], childKey),
					);
				}
			} else {
				summary = formatJsonPrimitive(currentValue as JsonPrimitive);
			}

			return {
				key: nodeKey,
				selectable: false,
				title: (
					<JsonTreeNodeTitle
						label={label}
						summary={summary}
						canAdd={isContainer}
						canEdit={!isContainer}
						canDelete={path.length > 0}
						isExpandable={isContainer}
						onAdd={
							isContainer
								? () => {
										handleOpenAddEditor(
											path,
											valueType === 'array' ? 'array' : 'object',
										);
									}
								: undefined
						}
						onEdit={
							!isContainer
								? () => {
										handleOpenEditEditor(path);
									}
								: undefined
						}
						onDelete={
							path.length > 0
								? () => {
										handleDeleteNode(path);
									}
								: undefined
						}
						onActivate={() => {
							onNodeActivate?.(nodeKey);
						}}
	onToggleExpand={
							isContainer
								? () => {
										setExpandedKeys((keys) =>
											keys.includes(nodeKey)
												? keys.filter((item) => item !== nodeKey)
												: [...keys, nodeKey],
										);
										setAutoExpandParent(false);
									}
								: undefined
						}
					/>
				),
				children,
			};
		};

		return [buildNode(value, [], ROOT_KEY)];
	}, [
		handleDeleteNode,
		handleOpenAddEditor,
		handleOpenEditEditor,
		onNodeActivate,
		value,
	]);

	const handleExpandAll = () => {
		if (value === null) {
			return;
		}

		const nextKeys: string[] = [];
		collectExpandableKeys(value, [], nextKeys);
		setExpandedKeys(nextKeys);
		setAutoExpandParent(true);
	};

	const handleCollapseAll = () => {
		if (value === null) {
			setExpandedKeys([]);
			return;
		}

		setExpandedKeys([ROOT_KEY]);
		setAutoExpandParent(false);
	};

	useEffect(() => {
		if (!editorState) {
			form.resetFields();
			return;
		}

		form.setFieldsValue({
			key: editorState.initialKey,
			type: editorState.initialType,
			value: editorState.initialValue,
		});
	}, [editorState, form]);

	const currentEditorType = Form.useWatch('type', form) as
		| JsonValueType
		| undefined;

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: 16,
				width: '100%',
				height,
				minHeight: 0,
			}}
		>
			<Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
				<Text strong>JSON 结构</Text>
				<Space>
					<Button
						size="small"
						onClick={handleExpandAll}
						disabled={disabled || value === null}
					>
						展开全部
					</Button>
					<Button
						size="small"
						onClick={handleCollapseAll}
						disabled={disabled || value === null}
					>
						收起全部
					</Button>
				</Space>
			</Space>

			{value === null ? (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description="暂无 JSON 内容"
				/>
			) : (
				<div
					style={{
						maxWidth: 720,
						width: '100%',
						margin: '0 auto',
						flex: 1,
						minHeight: 0,
						overflow: 'auto',
					}}
				>
					<Tree
						blockNode
						showLine
						treeData={treeData}
						expandedKeys={expandedKeys}
						autoExpandParent={autoExpandParent}
						onExpand={(keys) => {
							setExpandedKeys(keys as string[]);
							setAutoExpandParent(false);
						}}
						style={{
							fontSize: 13,
						}}
					/>
				</div>
			)}

			<Modal
				open={editorState !== null}
				title={editorState?.mode === 'add' ? '新增节点' : '编辑节点'}
				onCancel={() => setEditorState(null)}
				onOk={() => void handleConfirmEditor()}
				width={520}
				okButtonProps={{ disabled }}
				destroyOnClose
			>
				<Form form={form} layout="vertical">
					{editorState?.mode === 'add' &&
					editorState.parentKind === 'object' ? (
						<Form.Item
							label="字段名"
							name="key"
							rules={[{ required: true, message: '请输入字段名' }]}
						>
							<Input placeholder="例如：version" />
						</Form.Item>
					) : null}

					<Form.Item
						label="类型"
						name="type"
						rules={[{ required: true, message: '请选择类型' }]}
					>
						<Select
							options={
								editorState?.mode === 'edit'
									? VALUE_TYPES.filter(
											(type) => type !== 'object' && type !== 'array',
										).map((type) => ({
											label: getJsonTypeLabel(type),
											value: type,
										}))
									: VALUE_TYPES.map((type) => ({
											label: getJsonTypeLabel(type),
											value: type,
										}))
							}
						/>
					</Form.Item>

					{currentEditorType === 'string' ? (
						<Form.Item
							label="值"
							name="value"
							rules={[{ required: true, message: '请输入值' }]}
						>
							<Input placeholder="请输入字符串" />
						</Form.Item>
					) : null}

					{currentEditorType === 'number' ? (
						<Form.Item
							label="值"
							name="value"
							rules={[{ required: true, message: '请输入数字' }]}
						>
							<InputNumber style={{ width: '100%' }} />
						</Form.Item>
					) : null}

					{currentEditorType === 'boolean' ? (
						<Form.Item
							label="值"
							name="value"
							rules={[{ required: true, message: '请选择值' }]}
						>
							<Select
								options={[
									{ label: 'true', value: true },
									{ label: 'false', value: false },
								]}
							/>
						</Form.Item>
					) : null}

					{currentEditorType === 'object' || currentEditorType === 'array' ? (
						<div
							style={{
								padding: 12,
								borderRadius: 8,
								background: 'var(--ant-color-fill-tertiary)',
							}}
						>
							<Text type="secondary">
								当前类型会创建一个空的{getJsonTypeLabel(currentEditorType)}
								，后续可继续在树中新增子项。
							</Text>
						</div>
					) : null}
				</Form>
			</Modal>
		</div>
	);
}
